import { useState } from 'preact/hooks';
import { ModalNavigation } from './modal-navigation';
import { ModalSlide } from './modal-slide';
import { VNode } from 'preact';
import {
  SlideFive,
  SlideFour,
  SlideOne,
  SlideThree,
  SlideTwo
} from './slide-content';

interface ModalContentProps {
  impactOffer: {
    storeName: string;
    causeName: string;
    charityName: string;
    charityLogo: string;
    charityBackground: string;
    charityImpactMessage: string;
    causeBackground: string;
    globalBanner: string;
    donationString: string;
  };
  calculatedDonation: string;
  slideIndex: number;
  onSlideIndexChange: (i: number) => void;
  onClose: (e: MouseEvent) => void;
}

export const ModalContent = ({
  calculatedDonation,
  impactOffer,
  slideIndex,
  onSlideIndexChange,
  onClose
}: ModalContentProps) => {
  const {
    storeName,
    causeName,
    charityName,
    charityLogo,
    charityBackground,
    charityImpactMessage,
    causeBackground,
    globalBanner,
    donationString
  } = impactOffer;

  const donation = calculatedDonation ? calculatedDonation : donationString;

  const slides: VNode[] = [
    <SlideOne storeName={storeName} donationString={donation} />,
    <SlideTwo
      causeName={causeName}
      charityName={charityName}
      charityLogo={charityLogo}
      charityBackground={charityBackground}
    />,
    <SlideThree
      charityName={charityName}
      charityImpactMessage={charityImpactMessage}
      customerCount={0}
      causeBackground={causeBackground}
    />,
    <SlideFour globalBanner={globalBanner} donationString={donation} />,
    <SlideFive donationString={donation} />
  ];

  // for desktop pointer click/drag and mobile swiping
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragTranslate, setDragTranslate] = useState(0);

  // width of one slide
  const CLAMP_DISTANCE = 380;

  const handlePointerDown = (e: PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.clientX);
  };

  const handlePointerMove = (e: PointerEvent) => {
    if (!isDragging) return;
    const currentX = e.clientX;
    let delta = currentX - dragStartX;

    if (delta > CLAMP_DISTANCE) delta = CLAMP_DISTANCE;
    if (delta < -CLAMP_DISTANCE) delta = -CLAMP_DISTANCE;

    setDragTranslate(delta);
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    if (dragTranslate > 50 && slideIndex > 0) {
      // swiped right
      onSlideIndexChange(slideIndex - 1);
    } else if (dragTranslate < -50 && slideIndex < slides.length - 1) {
      // swiped left
      onSlideIndexChange(slideIndex + 1);
    }
    setDragTranslate(0);
  };


  // track pad swiping, generated from wheel events
  const WHEEL_THRESHOLD = 1200;
  const WHEEL_COOLDOWN_MS = 400; // how long to ignore subsequent wheel events

  const [, setAccumulatedWheel] = useState(0);
  const [wheelCooldown, setWheelCooldown] = useState(false);

  const handleWheel = (e: WheelEvent) => {
    if (wheelCooldown) return;

    // only if horizontal movement is bigger than vertical
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();

      setAccumulatedWheel((prev) => {
        const newValue = prev + e.deltaX;

        if (newValue < -WHEEL_THRESHOLD && slideIndex > 0) {
          // swiped left
          onSlideIndexChange(slideIndex - 1);
          enterWheelCooldown();
          return 0;
        } else if (newValue > WHEEL_THRESHOLD && slideIndex < slides.length - 1) {
          // swiped right
          onSlideIndexChange(slideIndex + 1);
          enterWheelCooldown();
          return 0;
        }

        return newValue;
      });
    }
  };

  // cooldown to prevent too many wheels events
  const enterWheelCooldown = () => {
    setWheelCooldown(true);
    // allow next swipe
    setTimeout(() => {
      setWheelCooldown(false);
    }, WHEEL_COOLDOWN_MS);
  };

  return (
    <div className="modal-boxes-container" onClick={onClose}>
      <ModalNavigation onChange={onSlideIndexChange} index={slideIndex} />

      <div
        className="modal-boxes-wrapper"
        style={{
          transform: `translateX(calc(-${slideIndex * 380}px + ${dragTranslate}px))`,
          transition: isDragging ? 'none' : 'transform 0.3s ease',
          overscrollBehavior: 'none',
          touchAction: 'none',
          userSelect: 'none'
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onWheel={handleWheel}
      >
        {slides.map((slide, i) => (
          <ModalSlide key={i} isTransparent={i === 1 || i === 2}>
            {slide}
          </ModalSlide>
        ))}
      </div>
    </div>
  );
};
